import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';

import { ActivityMessage } from './activity-messages';
import type { ContactActivity } from './types';
import type { Id } from '@minty/shared/ids';
import { nextCursorOf } from '~/hooks/use-paginated-list';
import { formatDay, localDayKey } from '~/lib/dates';
import { useTRPC } from '~/lib/trpc';

const PAGE_SIZE = 20;
const DEFAULT_ITEM_HEIGHT = 192;
const INITIAL_FIRST_ITEM_INDEX = 1_000_000;
const feedComponents = {
  Header: FeedTopSpacer,
  Footer: FeedFooter,
};

type FeedItem =
  | { kind: 'separator'; key: string; label: string }
  | { kind: 'activity'; activity: ContactActivity };

/**
 * The contact comes from the caller rather than the route: this feed is shown
 * by both inboxes, and reading a route by name binds it to one of them.
 */
export function ActivitiesFeed({
  contactId,
  contactName,
}: {
  contactId: Id<'contacts'>;
  contactName: string;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery(
      trpc.activities.feed.listContactActivities.infiniteQueryOptions(
        { contactId, limit: PAGE_SIZE },
        { getNextPageParam: nextCursorOf },
      ),
    );
  const activities = React.useMemo(
    () => (data?.pages.flatMap((page) => page.items) ?? []).reverse(),
    [data],
  );
  const isLoading = isPending;
  const isLoadingMore = isFetchingNextPage;
  const isExhausted = !isPending && !hasNextPage;
  const feedItems = React.useMemo(() => {
    const items: Array<FeedItem> = [];

    activities.forEach((activity, index) => {
      const dayKey = localDayKey(activity.createdAt);
      const previous = activities[index - 1];
      const isFirstOfDay =
        previous === undefined || dayKey !== localDayKey(previous.createdAt);

      if (isFirstOfDay && dayKey !== '' && (index > 0 || isExhausted)) {
        items.push({
          kind: 'separator',
          key: `day-${dayKey}`,
          label: formatDay(activity.createdAt),
        });
      }

      items.push({ kind: 'activity', activity });
    });

    return items;
  }, [activities, isExhausted]);
  /**
   * The activity Virtuoso counts from. Pinned to the oldest one the first page
   * brought, so loading older pages prepends above it rather than shifting
   * everything on screen. Held in state and adjusted during render, which is
   * how a value derived from props is kept without an Effect.
   */
  const oldestLoadedId = activities[0]?.activityId ?? null;
  const [anchor, setAnchor] = React.useState<{
    contactId: string;
    activityId: string | null;
  } | null>(null);

  if (
    anchor?.contactId !== contactId ||
    (anchor.activityId === null && oldestLoadedId !== null)
  ) {
    setAnchor({ contactId, activityId: oldestLoadedId });
  }

  const anchorId =
    anchor?.contactId === contactId ? anchor.activityId : oldestLoadedId;
  const anchorIndex = feedItems.findIndex(
    (item) => item.kind === 'activity' && item.activity.activityId === anchorId,
  );
  const firstItemIndex = INITIAL_FIRST_ITEM_INDEX - Math.max(0, anchorIndex);
  const handleStartReached = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  /** A page can come back empty when everything on it was filed away, and an
   *  empty list has no top for `startReached` to fire at. */
  const isEmptyButUnfinished =
    !isPending && activities.length === 0 && hasNextPage;

  React.useEffect(() => {
    if (isEmptyButUnfinished && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [isEmptyButUnfinished, isFetchingNextPage, fetchNextPage]);

  if (isLoading || isEmptyButUnfinished) {
    return (
      <main
        aria-busy="true"
        className="flex h-full min-w-0 items-center justify-center overflow-auto bg-background px-6 py-6"
      >
        <Loader2
          className="size-5 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      </main>
    );
  }

  if (isExhausted && activities.length === 0) {
    return (
      <main className="flex h-full min-w-0 flex-1 items-center justify-center overflow-auto bg-background p-8 text-center">
        <p className="text-sm text-muted-foreground">{t('No activities')}</p>
      </main>
    );
  }

  return (
    <main
      aria-busy={isLoadingMore}
      className="relative h-full min-w-0 bg-background"
    >
      {isLoadingMore && (
        <div
          className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-center"
          aria-hidden="true"
        >
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      )}
      <Virtuoso
        key={contactId}
        data={feedItems}
        defaultItemHeight={DEFAULT_ITEM_HEIGHT}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={{
          index: Math.max(0, feedItems.length - 1),
          align: 'end',
        }}
        alignToBottom
        followOutput="auto"
        atBottomThreshold={80}
        // `followOutput` re-pins to the newest message whenever the viewport
        // shrinks, which is what the composer does every time it grows. Left to
        // itself that re-pin lands an animation frame late, so the older
        // messages paint first and the feed appears to jump.
        skipAnimationFrameInResizeObserver
        startReached={handleStartReached}
        increaseViewportBy={{ top: 600, bottom: 600 }}
        minOverscanItemCount={{ top: PAGE_SIZE, bottom: 4 }}
        computeItemKey={(_index, item) =>
          item.kind === 'separator' ? item.key : item.activity.activityId
        }
        className="h-full [overflow-anchor:none]"
        components={feedComponents}
        itemContent={(_index, item) => (
          <>
            {/* The separator rules off the whole feed, so it is the one row
                that ignores the column the messages are held to. */}
            {item.kind === 'separator' && (
              <div className="px-6 pb-4">
                <DaySeparator label={item.label} />
              </div>
            )}
            {item.kind === 'activity' && (
              <div className="mx-auto w-full max-w-feed px-6 pb-4">
                <ActivityMessage
                  activity={item.activity}
                  contactId={contactId}
                  contactName={contactName}
                />
              </div>
            )}
          </>
        )}
      />
    </main>
  );
}

function FeedTopSpacer() {
  return <div className="h-6" aria-hidden="true" />;
}

function FeedFooter() {
  return <div className="h-2" aria-hidden="true" />;
}

function DaySeparator({ label }: { label: string }) {
  if (!label) {
    return null;
  }

  return (
    <div className="flex items-center">
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 whitespace-nowrap rounded-md bg-background px-2 py-0.5 text-xs text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
